import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import axiosInstance from '../utils/axiosConfig';
import useTranslation from '../utils/useTranslation';
import aboutContent from '../data/aboutContent';
import Header from './Header';
import Footer from './Footer';
import { Block } from './sections/AboutSection';

const PresidentUserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" />
    <path d="M5.5 20.5c.7-3.2 3.3-5.5 6.5-5.5s5.8 2.3 6.5 5.5" />
  </svg>
);

function letterToBlocks(letter, speakerName, role) {
  const text = (letter || '').trim();
  if (!text) return [];

  const byParagraph = text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  const paragraphs = byParagraph.length > 1
    ? byParagraph
    : text.split(/\n/).map((s) => s.trim()).filter(Boolean);

  const blocks = paragraphs.map((p) => ({ type: 'p', text: p }));
  if (speakerName) {
    blocks.push({ type: 'signature', name: speakerName, role });
  }
  return blocks;
}

export default function AboutPage() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams();
  const [chairman, setChairman] = useState({
    loading: true,
    name: '',
    letter: '',
    photoUrl: '',
  });
  const [photoFailed, setPhotoFailed] = useState(false);

  const tabs = aboutContent[lang] || aboutContent.ar;
  const active = tabs.find((item) => item.id === tab) || tabs[0];

  useEffect(() => {
    let alive = true;
    setChairman((prev) => ({ ...prev, loading: true }));
    axiosInstance
      .get('public/system-static/', { params: { lang } })
      .then((res) => {
        if (!alive) return;
        const data = res.data || {};
        setChairman({
          loading: false,
          name: data.speaker_name || '',
          letter: data.speaker_letter || '',
          photoUrl: data.speaker_photo_url || '',
        });
      })
      .catch(() => {
        if (!alive) return;
        setChairman({ loading: false, name: '', letter: '', photoUrl: '' });
      });
    return () => {
      alive = false;
    };
  }, [lang]);

  useEffect(() => {
    setPhotoFailed(false);
  }, [chairman.photoUrl]);

  const showPresidentPhoto = !!(chairman.photoUrl && !photoFailed);

  const chairmanBlocks = useMemo(
    () => letterToBlocks(chairman.letter, chairman.name, t('president_role')),
    [chairman.letter, chairman.name, t],
  );

  const bodyBlocks = active.id === 'chairman' ? chairmanBlocks : active.blocks;

  // Keep the URL canonical and scroll to top whenever the active tab changes.
  useEffect(() => {
    if (!tab || !tabs.some((item) => item.id === tab)) {
      navigate(`/about/${tabs[0].id}`, { replace: true });
    }
  }, [tab, tabs, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [active.id]);

  // Nav items on this page route back to the homepage and scroll to the
  // requested section (handled by HomePage via router state).
  const handleNavigate = (id, sub) => {
    if (id === 'about' && sub) {
      navigate(`/about/${sub}`);
      return;
    }
    if (id === 'home') {
      navigate('/');
      return;
    }
    navigate('/', { state: { scrollTo: id } });
  };

  return (
    <div className="about-page">
      <Header active="about" onNavigate={handleNavigate} />

      <main className="container about-page-wrap">
        <div className="about-page-grid">
          <aside className="about-page-side">
            <nav className="about-side-nav" aria-label={t('about_page_nav')}>
              {tabs.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`about-side-link${item.id === active.id ? ' is-active' : ''}`}
                  aria-current={item.id === active.id ? 'true' : undefined}
                  onClick={() => navigate(`/about/${item.id}`)}
                >
                  <span className="about-side-chevron" aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 6 15 12 9 18" />
                    </svg>
                  </span>
                  <span>{item.title}</span>
                </button>
              ))}
            </nav>

            {active.id === 'chairman' && (
              <figure className="about-president">
                <div className="about-president-photo">
                  {showPresidentPhoto ? (
                    <img
                      src={chairman.photoUrl}
                      alt={chairman.name || t('president_role')}
                      loading="lazy"
                      onError={() => setPhotoFailed(true)}
                    />
                  ) : (
                    <div className="about-president-photo-placeholder" aria-hidden="true">
                      <PresidentUserIcon />
                    </div>
                  )}
                </div>
                <figcaption className="about-president-caption">
                  {chairman.name && (
                    <span className="about-president-name">{chairman.name}</span>
                  )}
                  <span className="about-president-role">{t('president_role')}</span>
                </figcaption>
              </figure>
            )}
          </aside>

          <article className="about-page-main">
            <h1 className="about-page-title">{active.title}</h1>
            <div className="about-page-body">
              {active.id === 'chairman' && chairman.loading ? (
                <p className="center-note">{t('loading')}</p>
              ) : bodyBlocks.length === 0 && active.id === 'chairman' ? (
                <p className="center-note">{t('chairman_message_empty')}</p>
              ) : (
                bodyBlocks.map((block, i) => <Block key={i} block={block} />)
              )}
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
